import { useState, useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Order } from "@/hooks/useOrders";
import { format } from "date-fns";
import { nl } from "date-fns/locale";
import { ChevronRight, ChevronDown, ChevronsUpDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";

interface OrdersTableProps {
  orders: Order[] | undefined;
  isLoading: boolean;
  onOrderClick?: (ordernumber: string) => void;
}

interface OrderGroup {
  ordernumber: string;
  orders: Order[];
  totalItems: number;
  date: string | null;
}

const OrdersTable = ({ orders, isLoading, onOrderClick }: OrdersTableProps) => {
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [allExpanded, setAllExpanded] = useState(false);
  const isMobile = useIsMobile();

  // Group and sort orders
  const orderGroups = useMemo(() => {
    if (!orders || orders.length === 0) return [];

    const grouped = orders.reduce((acc, order) => {
      const key = order.ordernumber;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(order);
      return acc;
    }, {} as Record<string, Order[]>);

    const groups: OrderGroup[] = Object.entries(grouped).map(([ordernumber, orderList]) => {
      const firstOrder = orderList[0];
      const totalItems = orderList.reduce((sum, o) => sum + (o.quantityordered || 0), 0);

      return {
        ordernumber,
        orders: orderList,
        totalItems,
        date: firstOrder.createdat,
      };
    });

    groups.sort((a, b) => {
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });

    return groups;
  }, [orders]);

  const toggleOrder = (ordernumber: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(ordernumber)) {
        next.delete(ordernumber);
      } else {
        next.add(ordernumber);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (allExpanded) {
      setExpandedOrders(new Set());
    } else {
      setExpandedOrders(new Set(orderGroups.map(g => g.ordernumber)));
    }
    setAllExpanded(!allExpanded);
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <div className="rounded-md border p-8 text-center text-muted-foreground">
        Geen orders gevonden
      </div>
    );
  }

  // Mobile card layout
  if (isMobile) {
    return (
      <div className="space-y-2">
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={toggleAll} className="gap-1.5">
            <ChevronsUpDown className="h-4 w-4" />
            {allExpanded ? "Inklappen" : "Uitklappen"}
          </Button>
        </div>

        <div className="space-y-2">
          {orderGroups.map((group) => {
            const isExpanded = expandedOrders.has(group.ordernumber);
            return (
              <Card key={group.ordernumber} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleOrder(group.ordernumber)}>
                  <CollapsibleTrigger asChild>
                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge
                            variant="outline"
                            className="font-mono font-medium text-xs cursor-pointer hover:bg-primary/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              onOrderClick?.(group.ordernumber);
                            }}
                          >
                            {group.ordernumber}
                          </Badge>
                          <Badge variant="secondary" className="font-mono text-xs">
                            {group.totalItems}x
                          </Badge>
                        </div>
                        {group.date && (
                          <p className="text-xs text-muted-foreground">
                            {format(new Date(group.date), "d MMM", { locale: nl })}
                          </p>
                        )}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="border-t divide-y">
                      {group.orders.map((order) => (
                        <div key={order.id} className="flex items-center gap-3 p-3 bg-background">
                          {order.image_url && (
                            <img
                              src={order.image_url}
                              alt=""
                              className="w-10 h-10 object-cover rounded border flex-shrink-0"
                              onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{order.producttitle || "-"}</p>
                            {order.varianttitle && (
                              <p className="text-xs text-muted-foreground truncate">{order.varianttitle}</p>
                            )}
                          </div>
                          <span className="text-sm font-medium flex-shrink-0">{order.quantityordered || 0}x</span>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  // Desktop table layout
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={toggleAll} className="gap-1.5">
          <ChevronsUpDown className="h-4 w-4" />
          {allExpanded ? "Alles inklappen" : "Alles uitklappen"}
        </Button>
      </div>

      <div className="rounded-md border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead className="w-[120px]">Order</TableHead>
              <TableHead className="w-[100px]">Datum</TableHead>
              <TableHead className="text-right w-[80px]">Items</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orderGroups.map((group) => {
              const isExpanded = expandedOrders.has(group.ordernumber);
              return (
                <Collapsible key={group.ordernumber} open={isExpanded} onOpenChange={() => toggleOrder(group.ordernumber)} asChild>
                  <>
                    <CollapsibleTrigger asChild>
                      <TableRow className="bg-muted/50 hover:bg-muted cursor-pointer transition-colors">
                        <TableCell className="py-3">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="font-mono font-medium cursor-pointer hover:bg-primary/10 transition-colors" onClick={(e) => { e.stopPropagation(); onOrderClick?.(group.ordernumber); }}>
                            {group.ordernumber}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {group.date ? <span className="text-sm">{format(new Date(group.date), "d MMM", { locale: nl })}</span> : <span className="text-muted-foreground">-</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <Badge variant="secondary" className="font-mono">{group.totalItems}x</Badge>
                        </TableCell>
                      </TableRow>
                    </CollapsibleTrigger>
                    <CollapsibleContent asChild>
                      <>
                        {group.orders.map((order) => (
                          <TableRow key={order.id} className="bg-background">
                            <TableCell></TableCell>
                            <TableCell colSpan={5}>
                              <div className="flex items-center gap-4 py-1">
                                {order.image_url && (
                                  <img src={order.image_url} alt="" className="w-10 h-10 object-cover rounded border flex-shrink-0" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{order.producttitle || "-"}</p>
                                  {order.varianttitle && <p className="text-sm text-muted-foreground truncate">{order.varianttitle}</p>}
                                </div>
                                <div className="text-sm font-mono text-muted-foreground w-24">{order.sku || order.artikel_code || "-"}</div>
                                <div className="text-sm font-mono text-muted-foreground w-28">{order.ean || "-"}</div>
                                <div className="w-20">
                                  {order.customfieldcolor ? <Badge variant="secondary" className="text-xs">{order.customfieldcolor}</Badge> : <span className="text-muted-foreground text-sm">-</span>}
                                </div>
                                <div className="w-12 text-right font-medium">{order.quantityordered || 0}x</div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </>
                    </CollapsibleContent>
                  </>
                </Collapsible>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default OrdersTable;
